import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Download, Save, ShieldAlert, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RISK_OPTS = ["Low", "Medium", "High"];

// normalize a list that may be strings or {label,checked}
const toItems = (arr) => (arr || []).map((x) => (typeof x === "string" ? { label: x, checked: false } : { label: x.label || "", checked: !!x.checked }));

export default function SwmsDialog({ open, onOpenChange, mode, jobId }) {
  const isJob = mode === "job";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isJob ? `/jobs/${jobId}/swms` : "/swms/template";
      const r = await api.get(url);
      const d = r.data;
      d.high_risk_work = toItems(d.high_risk_work);
      d.ppe = toItems(d.ppe);
      d.steps = d.steps || [];
      d.legislation = d.legislation || [];
      d.hierarchy_of_controls = d.hierarchy_of_controls || [];
      d.signatories = d.signatories || [];
      d.company = d.company || { name: "Air Log" };
      setData(d);
    } catch { toast.error("Could not load SWMS"); }
    finally { setLoading(false); }
  }, [isJob, jobId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setStep = (i, k, v) => setData((d) => ({ ...d, steps: d.steps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addStep = () => setData((d) => ({ ...d, steps: [...d.steps, { task: "", hazards: "", initial_risk: "Medium", controls: "", residual_risk: "Low", responsible: "" }] }));
  const rmStep = (i) => setData((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }));
  const setListItem = (key, i, patch) => setData((d) => ({ ...d, [key]: d[key].map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));
  const addListItem = (key) => setData((d) => ({ ...d, [key]: [...d[key], { label: "", checked: false }] }));
  const rmListItem = (key, i) => setData((d) => ({ ...d, [key]: d[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      if (isJob) {
        await api.put(`/jobs/${jobId}/swms`, data);
        toast.success("SWMS saved to job");
      } else {
        const payload = { ...data, high_risk_work: data.high_risk_work.map((x) => x.label).filter(Boolean), ppe: data.ppe.map((x) => x.label).filter(Boolean) };
        await api.put("/swms/template", payload);
        toast.success("SWMS template saved");
      }
      onOpenChange(false);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("session_token");
    window.open(`${BACKEND}/api/jobs/${jobId}/swms/pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="swms-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-lime-500" /> {isJob ? "Job Safety Document (SWMS)" : "SWMS Master Template"}</DialogTitle>
          <DialogDescription>{isJob ? "Pre-filled from this job. Edit, save a copy to the job, and download the PDF." : "This is your reusable template. It pre-fills every job's SWMS."}</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Job header (job mode) */}
            {isJob && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm" data-testid="swms-job-header">
                <Field label="SWMS No." value={data.swms_no} onChange={(v) => set("swms_no", v)} />
                <Field label="Date" value={data.date} onChange={(v) => set("date", v)} type="date" />
                <Field label="Client" value={data.client} onChange={(v) => set("client", v)} />
                <Field label="Project / Site" value={data.project} onChange={(v) => set("project", v)} />
                <Field label="Location" value={data.location} onChange={(v) => set("location", v)} />
                <Field label="Pilot in Command" value={data.pic} onChange={(v) => set("pic", v)} />
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Crew</Label>
                  <Input value={(data.crew || []).join(", ")} onChange={(e) => set("crew", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="mt-1 h-8" />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">RPA / Equipment</Label>
                  <Input value={(data.rpa || []).join(", ")} onChange={(e) => set("rpa", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="mt-1 h-8" />
                </div>
              </div>
            )}

            {/* Company (template mode) */}
            {!isJob && (
              <Section title="Business / PCBU details">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Company" value={data.company.name} onChange={(v) => set("company", { ...data.company, name: v })} />
                  <Field label="ABN" value={data.company.abn} onChange={(v) => set("company", { ...data.company, abn: v })} />
                  <Field label="Contact" value={data.company.contact} onChange={(v) => set("company", { ...data.company, contact: v })} />
                  <Field label="Phone" value={data.company.phone} onChange={(v) => set("company", { ...data.company, phone: v })} />
                </div>
              </Section>
            )}

            <Section title="Title & activity">
              <Field label="Document title" value={data.title} onChange={(v) => set("title", v)} />
              <Textarea value={data.activity} onChange={(e) => set("activity", e.target.value)} rows={2} className="mt-2" placeholder="Activity description" />
            </Section>

            <Section title={`High-risk work ${isJob ? "(tick what applies)" : "(template list)"}`}>
              {data.high_risk_work.map((it, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`swms-hrw-${i}`}>
                  {isJob && <input type="checkbox" checked={it.checked} onChange={(e) => setListItem("high_risk_work", i, { checked: e.target.checked })} className="accent-lime-500 h-4 w-4" data-testid={`swms-hrw-check-${i}`} />}
                  <Input value={it.label} onChange={(e) => setListItem("high_risk_work", i, { label: e.target.value })} className="flex-1 h-8" readOnly={isJob} />
                  {!isJob && <IconBtn onClick={() => rmListItem("high_risk_work", i)} />}
                </div>
              ))}
              {!isJob && <AddBtn onClick={() => addListItem("high_risk_work")} label="Add item" />}
            </Section>

            <Section title="PPE">
              {data.ppe.map((it, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`swms-ppe-${i}`}>
                  {isJob && <input type="checkbox" checked={it.checked} onChange={(e) => setListItem("ppe", i, { checked: e.target.checked })} className="accent-lime-500 h-4 w-4" />}
                  <Input value={it.label} onChange={(e) => setListItem("ppe", i, { label: e.target.value })} className="flex-1 h-8" readOnly={isJob} />
                  {!isJob && <IconBtn onClick={() => rmListItem("ppe", i)} />}
                </div>
              ))}
              {!isJob && <AddBtn onClick={() => addListItem("ppe")} label="Add PPE" />}
            </Section>

            <Section title="Work steps, hazards & controls">
              <div className="space-y-3">
                {data.steps.map((s, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`swms-step-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-slate-400">{i + 1}</span>
                      <Input value={s.task} onChange={(e) => setStep(i, "task", e.target.value)} placeholder="Task / step" className="flex-1 h-8 font-medium" />
                      <IconBtn onClick={() => rmStep(i)} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Textarea value={s.hazards} onChange={(e) => setStep(i, "hazards", e.target.value)} rows={2} placeholder="Hazards" className="text-sm" />
                      <Textarea value={s.controls} onChange={(e) => setStep(i, "controls", e.target.value)} rows={2} placeholder="Control measures" className="text-sm" />
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <RiskSel label="Initial" value={s.initial_risk} onChange={(v) => setStep(i, "initial_risk", v)} />
                      <RiskSel label="Residual" value={s.residual_risk} onChange={(v) => setStep(i, "residual_risk", v)} />
                      <div><Label className="text-[10px] uppercase text-slate-400">Responsible</Label><Input value={s.responsible} onChange={(e) => setStep(i, "responsible", e.target.value)} className="h-8" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <AddBtn onClick={addStep} label="Add step" />
            </Section>

            <Section title="Emergency response">
              <Textarea value={data.emergency} onChange={(e) => set("emergency", e.target.value)} rows={3} />
            </Section>

            {isJob && (
              <Section title="Personnel sign-off">
                {(data.signatories || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`swms-sig-${i}`}>
                    <Input value={p.name} onChange={(e) => set("signatories", data.signatories.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="h-8" />
                    <Input value={p.role} onChange={(e) => set("signatories", data.signatories.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} placeholder="Role" className="h-8" />
                    <Input type="date" value={p.date} onChange={(e) => set("signatories", data.signatories.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} className="h-8 w-36" />
                    <IconBtn onClick={() => set("signatories", data.signatories.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                <AddBtn onClick={() => set("signatories", [...(data.signatories || []), { name: "", role: "", signature: "", date: "" }])} label="Add person" />
                <p className="text-xs text-slate-400 mt-1">The PDF includes blank signature rows for on-site signing.</p>
              </Section>
            )}

            <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-slate-100">
              {isJob && <Button variant="outline" onClick={downloadPdf} data-testid="swms-download-pdf"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button>}
              <Button onClick={save} disabled={saving} data-testid="swms-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                {isJob ? "Save to job" : "Save template"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {title}</h3>
    {children}
  </div>
);
const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8" />
  </div>
);
const RiskSel = ({ label, value, onChange }) => (
  <div>
    <Label className="text-[10px] uppercase text-slate-400">{label} risk</Label>
    <select value={value || "Low"} onChange={(e) => onChange(e.target.value)} className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm">
      {RISK_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const IconBtn = ({ onClick }) => (
  <button onClick={onClick} className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
);
const AddBtn = ({ onClick, label }) => (
  <Button variant="outline" size="sm" onClick={onClick} className="border-dashed mt-1"><Plus className="h-4 w-4 mr-1.5" /> {label}</Button>
);
